<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <h2 class="text-h5 font-weight-bold text-center mb-1">
                    Enter your code
                </h2>
                <p class="text-body-2 text-medium-emphasis text-center mb-6">
                    We sent a 6-digit code to <strong>{{ phone }}</strong>.
                    <br/>Didn't get it? Check your texts for a shortcode or try again.
                </p>

                <v-form v-model="formValid" validate-on="input lazy"
                        @submit.prevent="onVerify">
                    <v-text-field v-model="code"
                                  :rules="[rules.required, rules.sixDigits]"
                                  label="Verification code"
                                  placeholder="123456"
                                  prepend-inner-icon="mdi-numeric"
                                  type="tel"
                                  inputmode="numeric"
                                  maxlength="6"
                                  autocomplete="one-time-code"
                                  color="primary"
                                  density="compact"
                                  :disabled="submitting"/>

                    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4" density="compact">
                        {{ errorMessage }}
                    </v-alert>

                    <v-btn type="submit"
                           block color="primary" size="large"
                           class="mt-2"
                           :loading="submitting"
                           :disabled="!canSubmit">
                        Verify and continue
                    </v-btn>

                    <div class="text-center mt-4">
                        <v-btn variant="text" color="primary" size="small"
                               :disabled="resending || resendCooldown > 0"
                               :loading="resending"
                               @click="onResend">
                            <span v-if="resendCooldown > 0">Resend in {{ resendCooldown }}s</span>
                            <span v-else>Resend code</span>
                        </v-btn>
                    </div>

                    <div class="text-center mt-4">
                        <router-link to="/signup"
                                     class="text-body-2 text-primary text-decoration-none">
                            Use a different number
                        </router-link>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {apiErrorMessage} from "@/composables/useApiError";
import {useSnackbarStore} from "@/stores/snackBarStore";
import logoFull from "@/assets/images/logo-full.png";

const route = useRoute();
const router = useRouter();
const snackBar = useSnackbarStore();

const phone = ref(route.query.phone || "");
const code = ref("");
const formValid = ref(false);
const submitting = ref(false);
const resending = ref(false);
const errorMessage = ref("");

// Cooldown between resend clicks so users can't spam the send-otp
// endpoint from this screen.
const resendCooldown = ref(30);
let cooldownTimer = null;

const rules = {
    required: (v) => (!!v && String(v).trim() !== "") || "Required",
    sixDigits: (v) => /^\d{6}$/.test(String(v || "")) || "Enter the 6-digit code.",
};

const canSubmit = computed(() => formValid.value && !submitting.value);

onMounted(() => {
    // Send them back to phone entry if they deep-link here without a phone.
    if (!phone.value) {
        router.replace({name: "signup-phone"});
        return;
    }
    startCooldown();
});

onBeforeUnmount(() => {
    if (cooldownTimer) clearInterval(cooldownTimer);
});

function startCooldown(seconds = 30) {
    resendCooldown.value = Math.max(1, Math.min(600, Math.floor(seconds)));
    if (cooldownTimer) clearInterval(cooldownTimer);
    cooldownTimer = setInterval(() => {
        if (resendCooldown.value > 0) resendCooldown.value--;
        else {
            clearInterval(cooldownTimer);
            cooldownTimer = null;
        }
    }, 1000);
}

async function onVerify() {
    if (!canSubmit.value) return;

    submitting.value = true;
    errorMessage.value = "";

    try {
        const res = await send("post", "auth/phone/verify-otp", {
            phone: phone.value.trim(),
            code: code.value.trim(),
        });
        const data = res?.data?.data ?? {};
        if (res?.data?.success && data.verification_token) {
            snackBar.showSnackbar("Phone verified.", "success");
            router.push({
                name: "register",
                query: {
                    phone: phone.value.trim(),
                    verification_token: data.verification_token,
                },
            });
        } else {
            errorMessage.value = res?.data?.message
                || "Invalid or expired code. Please try again or request a new one.";
        }
    } catch (e) {
        const info = apiErrorMessage(e, "Invalid or expired code. Please try again or request a new one.");
        errorMessage.value = info.message;
    } finally {
        submitting.value = false;
    }
}

async function onResend() {
    if (resending.value || resendCooldown.value > 0) return;
    resending.value = true;
    try {
        // Re-issue from this page doesn't carry a CAPTCHA token - the
        // per-phone rate limit in PhoneVerificationService + the route-level
        // throttle make this safe as a user-initiated retry.
        const res = await send("post", "auth/phone/send-otp", {phone: phone.value.trim()});
        if (res?.data?.success) {
            snackBar.showSnackbar("New code sent.", "success");
            startCooldown();
        } else {
            errorMessage.value = res?.data?.message || "Could not resend. Please try again.";
        }
    } catch (e) {
        const info = apiErrorMessage(e, "Could not resend. Please try again.");
        errorMessage.value = info.message;
        if (info.isThrottled) {
            startCooldown(info.retryAfterSec || 60);
        }
    } finally {
        resending.value = false;
    }
}
</script>

<style scoped>
.wrapper {
    min-height: 100vh;
}
.login-card {
    width: 100%;
    max-width: 440px;
}
</style>
